import Stack from '@mui/material/Stack';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { validateProps } from '../../../utils/propValidator';
import {
  ControlledImageUpload,
  ControlledTextField,
  ErrorSummary,
  ModalWrapper,
  SectionTitle,
} from '../../shared/components';
import { postNewsArticle, updateNewsArticle } from '../news.service';

const propRules = {
  open: { type: 'boolean', required: true },
  onClose: { type: 'function', required: true },
  mode: { oneOf: ['add', 'edit'] },
  onSubmit: { type: 'function', required: true },
  article: { type: ['object', 'null'] },
};

const DEFAULT_VALUES = {
  title: '',
  content: '',
  referenceLink: '',
  photo: null,
};

export default function NewsEntryModal(props) {
  validateProps(props as Record<string, unknown>, propRules, 'NewsEntryModal');
  const { open, onClose, mode, onSubmit } = props;

  const [saving, setSaving] = useState(false);

  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange',
  });

  const { handleSubmit, reset } = methods;

  function normalizePhoto(photo) {
    if (!photo) return null;

    if (typeof photo === 'object' && photo.url) {
      return photo;
    }

    if (typeof photo === 'string') {
      return { url: photo };
    }

    if (typeof photo === 'object') {
      return { id: photo.id, url: photo.image_url || photo.url };
    }

    return null;
  }

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && props.article) {
      reset({
        ...DEFAULT_VALUES,
        ...props.article,
        photo: normalizePhoto(props.article.photo),
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [open, mode, props.article, reset]);

  const handleFormSubmit = async (data) => {
    setSaving(true);
    try {
      if (mode === 'edit') {
        const articleId = props.article.id;
        if (!articleId) return;
        await updateNewsArticle(articleId, data);
        toast.success('News article updated successfully');
      } else {
        await postNewsArticle(data);
        toast.success('News article submitted successfully');
      }
      onSubmit();
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to publish project to website',
          error: err,
          code: 'NEWS_ACTION_FAILED',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalWrapper open={open} onClose={() => (saving ? {} : onClose())} maxWidth='md'>
      <FormProvider {...methods}>
        <Stack component='form' onSubmit={handleSubmit(handleFormSubmit)} gap={3}>
          <SectionTitle title={mode === 'add' ? 'Add news article' : 'Edit news article'} />
          <ErrorSummary />

          <ControlledTextField
            id='title'
            name='title'
            label='Title'
            rules={{ required: 'Title is required' }}
            fullWidth
          />

          <ControlledTextField
            id='content'
            name='content'
            label='Content'
            rules={{
              required: 'Content is required',
              minLength: { value: 50, message: 'News article must be at least 50 characters' },
              maxLength: { value: 1000, message: 'News article must be at most 1000 characters' },
            }}
            fullWidth
            isMultiline
            showCharCount
            rows={4}
          />

          <ControlledTextField
            id='referenceLink'
            name='referenceLink'
            label='Reference Link'
            rules={{ required: 'Reference link is required' }}
            fullWidth
          />

          <div className='self-start'>
            <ControlledImageUpload
              id='photo'
              name='photo'
              rules={{ required: 'Image is required' }}
              buttonText='Upload photo'
              previewAfter
              maxFileSize={5}
              aspectRatio='16:9'
              uploaderTitle='Upload article photo'
              viewerTitle='Article photo'
            />
          </div>

          <button type='submit' className='btn cta-btn self-end' disabled={saving}>
            Publish article
          </button>
        </Stack>
      </FormProvider>
    </ModalWrapper>
  );
}
